// TS twin of iOS `JoystickPad.swift`.
// D-pad style positioning controls. Center button is a MODE TOGGLE:
//   pos   — arrows control height (up/down) and horizontal (left/right)
//   depth — up/down control depth (up = push into wheel well, down =
//           pull out); left/right slot shows depth-offset in inches
//   scale — up/down control scale (multiplicative); left/right slot
//           shows current scale as a percentage
// Cycle: pos → depth → scale → pos. Left/right arrows only rendered
// in pos mode; depth/scale modes use those slots for spacer + readout.

import { useCallback, useState } from 'react'
import { formatWheelDiameter } from '@/lib/formatWheelDiameter'
import { Repeat2 } from 'lucide-react'
import { RepeatButton } from '@/components/repeat-button/repeat-button'

interface JoystickPadProps {
  onUp: () => void
  onDown: () => void
  onLeft: () => void
  onRight: () => void
  /** Called with a signed delta each time up/down is pressed in depth
   *  mode. delta > 0 = pull wheel toward camera; delta < 0 = push
   *  wheel into the wheel-well (matches iOS convention). */
  onDepthDrag: (delta: number) => void
  /** Fixed step size (m) used when depth-mode up/down are pressed. */
  depthStep: number
  /** Called with a signed multiplicative delta each time up/down is
   *  pressed in scale mode. e.g. +0.05 = grow 5 %. */
  onScaleChange: (delta: number) => void
  scaleStep: number
  /** Current depth offset (m) — displayed in inches next to the mode
   *  button when in depth mode. */
  currentDepth: number
  /** Current scale multiplier (1.0 = 100 %) — displayed as a percent
   *  when in scale mode. */
  currentScale: number
  /** Nominal diameter of the loaded wheel in inches — parsed from
   *  base_part.meta_values.size ("DxW" → D). Defaults to 26 when the
   *  loader hasn't produced a size yet. Displayed under the % readout
   *  in scale mode as `(D × multiplier)"` rounded to 1 decimal. */
  originalDiameterInches?: number
  buttonSize?: number
}

type Mode = 'pos' | 'depth' | 'scale'

const NEXT_MODE: Record<Mode, Mode> = {
  pos: 'depth',
  depth: 'scale',
  scale: 'pos',
}

const M_TO_IN = 39.3701

export function JoystickPad({
  onUp,
  onDown,
  onLeft,
  onRight,
  onDepthDrag,
  depthStep,
  onScaleChange,
  scaleStep,
  currentDepth,
  currentScale,
  originalDiameterInches = 26,
  buttonSize = 43,
}: JoystickPadProps) {
  const spacing = 4
  const [mode, setMode] = useState<Mode>('pos')
  const cycleMode = useCallback(() => setMode((m) => NEXT_MODE[m]), [])

  const handleUp =
    mode === 'pos'
      ? onUp
      : mode === 'depth'
        ? () => onDepthDrag(-depthStep)
        : () => onScaleChange(scaleStep)
  const handleDown =
    mode === 'pos'
      ? onDown
      : mode === 'depth'
        ? () => onDepthDrag(depthStep)
        : () => onScaleChange(-scaleStep)

  const upLabel =
    mode === 'pos'
      ? 'Move wheel up'
      : mode === 'depth'
        ? 'Push wheel deeper'
        : 'Scale up'
  const downLabel =
    mode === 'pos'
      ? 'Move wheel down'
      : mode === 'depth'
        ? 'Pull wheel out'
        : 'Scale down'

  return (
    <div
      data-testid="joystick-pad"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing,
        alignItems: 'center',
      }}
    >
      <RepeatButton
        onPress={handleUp}
        ariaLabel={upLabel}
        style={{ width: buttonSize, height: buttonSize }}
      >
        ▲
      </RepeatButton>
      <div style={{ display: 'flex', gap: spacing, alignItems: 'center' }}>
        {mode === 'pos' ? (
          <RepeatButton
            onPress={onLeft}
            ariaLabel="Move wheel left"
            style={{ width: buttonSize, height: buttonSize }}
          >
            ◀
          </RepeatButton>
        ) : (
          <div style={{ width: buttonSize, height: buttonSize }} />
        )}
        <CenterModeToggle
          size={buttonSize}
          mode={mode}
          onToggle={cycleMode}
        />
        {mode === 'pos' ? (
          <RepeatButton
            onPress={onRight}
            ariaLabel="Move wheel right"
            style={{ width: buttonSize, height: buttonSize }}
          >
            ▶
          </RepeatButton>
        ) : (
          <SideReadout
            width={buttonSize}
            height={buttonSize}
            mode={mode}
            currentDepth={currentDepth}
            currentScale={currentScale}
            originalDiameterInches={originalDiameterInches}
          />
        )}
      </div>
      <RepeatButton
        onPress={handleDown}
        ariaLabel={downLabel}
        style={{ width: buttonSize, height: buttonSize }}
      >
        ▼
      </RepeatButton>
    </div>
  )
}

interface CenterModeToggleProps {
  size: number
  mode: Mode
  onToggle: () => void
}

function CenterModeToggle({ size, mode, onToggle }: CenterModeToggleProps) {
  return (
    <button
      type="button"
      aria-label={
        mode === 'pos'
          ? 'Switch to depth control'
          : mode === 'depth'
            ? 'Switch to scale control'
            : 'Switch to position control'
      }
      onClick={onToggle}
      style={{
        width: size,
        height: size,
        background: 'rgba(255, 255, 255, 0.15)',
        border: 'none',
        borderRadius: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        color: 'rgba(255, 255, 255, 0.85)',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.5,
        cursor: 'pointer',
        touchAction: 'none',
        userSelect: 'none',
      }}
    >
      {/* Label reflects CURRENT mode (what the arrows do right now).
       *  Icon below the label signals the button toggles. */}
      {mode === 'pos' ? 'POS' : mode === 'depth' ? 'DEPTH' : 'SCALE'}
      <Repeat2 size={12} strokeWidth={2} aria-hidden />
    </button>
  )
}

interface SideReadoutProps {
  width: number
  height: number
  mode: Mode
  currentDepth: number
  currentScale: number
  originalDiameterInches: number
}

/** Right-slot readout during depth/scale modes — signed value.
 *  Scale mode shows two lines: `+/- N%` above, scaled diameter in
 *  inches (1 decimal) below. */
function SideReadout({
  width,
  height,
  mode,
  currentDepth,
  currentScale,
  originalDiameterInches,
}: SideReadoutProps) {
  const readoutStyle = {
    width,
    height,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    color: 'rgba(255, 255, 255, 0.75)',
    fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: 0.3,
    pointerEvents: 'none' as const,
    userSelect: 'none' as const,
  }
  if (mode === 'depth') {
    const inches = currentDepth * M_TO_IN
    return (
      <div data-testid="joystick-side-readout" style={readoutStyle}>
        <span>{formatSigned(inches, 1) + ' in'}</span>
      </div>
    )
  }
  // scale mode: absolute % (100 % baseline) + scaled diameter (drops
  // trailing ".0" for whole-inch values so 26" reads clean).
  const pct = Math.round(currentScale * 100)
  const scaledDiameter = originalDiameterInches * currentScale
  const diameterDisplay = formatWheelDiameter(scaledDiameter) + '"'
  return (
    <div data-testid="joystick-side-readout" style={readoutStyle}>
      <span>{pct + '%'}</span>
      <span style={{ opacity: 0.85, fontSize: 10 }}>{diameterDisplay}</span>
    </div>
  )
}

function formatSigned(value: number, digits: number): string {
  const rounded =
    digits === 0 ? Math.round(value) : Number(value.toFixed(digits))
  if (rounded === 0) return '0'
  const abs = Math.abs(rounded).toFixed(digits)
  return (rounded > 0 ? '+' : '−') + abs
}
